import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Server-side session ended (idle timeout, "sign out everywhere", disabled login) → drop to the sign-in page
  useEffect(() => {
    const id = api.interceptors.response.use(
      (r) => r,
      (err) => {
        const url = err.config?.url || "";
        if (err.response?.status === 401 && !/^\/auth\/(login|mfa|me|signup)/.test(url) && !url.startsWith("/public")) {
          setUser((u) => (u ? false : u));
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(id);
  }, []);

  const login = async (email, password, portal) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, portal: portal || null });
      if (data.mfa_required) return { ok: false, mfa: data };   // second step: emailed code
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verifyMfa = async (challenge_id, code, remember_device) => {
    try {
      const { data } = await api.post("/auth/mfa/verify", { challenge_id, code, remember_device });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resendMfa = async (challenge_id) => {
    try {
      const { data } = await api.post("/auth/mfa/resend", { challenge_id });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, verifyMfa, resendMfa, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
